import { Col, Divider, Form, Input, InputNumber, Row, Space, Switch, Tag, Typography } from 'antd'
import useFormInstance from 'antd/es/form/hooks/useFormInstance'
import { NamePath } from 'antd/es/form/interface'
import _, { concat } from 'lodash'
import { FC, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import {
    requiredInputNumberRules,
    requiredRules,
} from '../../../../../../../constants/forms/validation/rules'
import UploadFile from '../../../../../../../components/form-items/upload-file/upload-file'
import getSumOfCopies from '../../../../../../../helpers/calculations/getters/get-sum-of-copies'
import formatIntegerBySpace from '../../../../../../../helpers/formatters/format-integer-by-space'
import FormValues from '../../../types/form-values'

interface PositionsProps {
    namePath: NamePath
    sourceUploadDisabled?: boolean
}

const LoPairDivider = styled(Divider)`
    && {
        margin: 8px 0 16px;
    }
`

const Positions: FC<PositionsProps> = ({ namePath, sourceUploadDisabled }) => {
    const form = useFormInstance<FormValues>()
    const tabIndex = namePath as number
    const name = _.concat(namePath, 'positions')
    const positionsPath = useMemo<['list', number, 'positions']>(
        () => ['list', tabIndex, 'positions'],
        [tabIndex]
    )
    const maxCopiesPath: ['list', number, 'calculationPosition', 'copies'] = [
        'list',
        tabIndex,
        'calculationPosition',
        'copies',
    ]
    const numberOfVariantsForDigitPath: [
        'list',
        number,
        'calculationPosition',
        'numberOfVariantsForDigit',
    ] = ['list', tabIndex, 'calculationPosition', 'numberOfVariantsForDigit']
    const positions = Form.useWatch(positionsPath, form)

    useEffect(() => {
        if (!form.getFieldError(positionsPath).length) {
            return
        }

        form.validateFields([positionsPath]).catch(() => undefined)
    }, [form, positions, positionsPath])

    const validateCopiesSum = async () => {
        const numberOfVariantsForDigit = form.getFieldValue(numberOfVariantsForDigitPath)
        if (!numberOfVariantsForDigit) {
            return Promise.resolve()
        }

        const currentPositions = form.getFieldValue(positionsPath) || []
        const maxCopies = form.getFieldValue(maxCopiesPath) || 0
        const totalCopies = getSumOfCopies(currentPositions) || 0

        if (totalCopies !== maxCopies) {
            return Promise.reject(
                `Сумма тиражей должна быть равна ${formatIntegerBySpace(maxCopies)}`
            )
        }

        return Promise.resolve()
    }

    const clearWindingSchemeTurnIfNoLo = () => {
        const currentPositions = form.getFieldValue(positionsPath) || []
        const hasLo = currentPositions.some(
            (pos: FormValues['list'][number]['positions'][number]) => pos?.side === 'face'
        )
        if (!hasLo) {
            form.setFieldValue(['list', tabIndex, 'windingSchemeTurn'], undefined)
        }
    }

    return (
        <Form.List name={name} rules={[{ validator: validateCopiesSum }]}>
            {(fields, { add, remove }, { errors }) => {
                const hasCopiesSumError = errors.length > 0

                return (
                    <>
                        {fields.map((item, i) => {
                            const nameName = concat(String(item.name), ['name'])
                            const copiesName = concat(String(item.name), ['copies'])
                            const commentName = concat(String(item.name), ['comment'])
                            const sourceFileName = concat(String(item.name), 'sourceFile')
                            const side = positions?.[i]?.side
                            const isTurn = side === 'turn'
                            const isFace = side === 'face'
                            const showLoSwitch = !isTurn
                            const kindNumber =
                                positions
                                    ?.slice(0, i + 1)
                                    .filter(position => position?.side !== 'turn').length || i + 1
                            const showDividerAfterLoPair = isTurn

                            const onLoChange = (checked: boolean) => {
                                const current = form.getFieldValue([...positionsPath, item.name])

                                if (checked) {
                                    form.setFieldValue([...positionsPath, item.name], {
                                        ...current,
                                        side: 'face',
                                    })
                                    add(
                                        {
                                            name: current?.name ?? '',
                                            copies: undefined,
                                            comment: current?.comment ?? '',
                                            sourceFile: current?.sourceFile,
                                            floodsInAccumulation: current?.floodsInAccumulation,
                                            side: 'turn',
                                        },
                                        i + 1
                                    )
                                    return
                                }

                                const next = form.getFieldValue([...positionsPath, i + 1])
                                if (next?.side === 'turn') {
                                    remove(i + 1)
                                }
                                form.setFieldValue([...positionsPath, item.name], {
                                    ...form.getFieldValue([...positionsPath, item.name]),
                                    side: undefined,
                                })
                                clearWindingSchemeTurnIfNoLo()
                            }

                            return (
                                <div key={item.key}>
                                    <Row gutter={16} align='top'>
                                        <Col flex='1 1 100px'>
                                            <Form.Item
                                                name={nameName}
                                                label={
                                                    <Space size={6} wrap={false}>
                                                        <span>{`Наименование ${kindNumber} вида`}</span>
                                                        {isFace ? (
                                                            <Tag color='blue'>Лицо</Tag>
                                                        ) : null}
                                                        {isTurn ? (
                                                            <Tag color='orange'>Оборот</Tag>
                                                        ) : null}
                                                    </Space>
                                                }
                                                rules={requiredRules}
                                            >
                                                <Input placeholder={''} />
                                            </Form.Item>
                                        </Col>
                                        <Col flex='1 1 50px'>
                                            <Form.Item
                                                name={copiesName}
                                                label={
                                                    <Space size={6} wrap={false}>
                                                        <span>{`Тираж ${kindNumber} вида`}</span>
                                                        {showLoSwitch ? (
                                                            <Switch
                                                                size='small'
                                                                checkedChildren='Л/О'
                                                                unCheckedChildren='Л/О'
                                                                checked={isFace}
                                                                onChange={onLoChange}
                                                            />
                                                        ) : null}
                                                    </Space>
                                                }
                                                rules={requiredInputNumberRules}
                                                validateStatus={
                                                    hasCopiesSumError ? 'error' : undefined
                                                }
                                            >
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    placeholder={''}
                                                    formatter={formatIntegerBySpace}
                                                    controls={false}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col flex='1 1 100px'>
                                            <Form.Item
                                                name={commentName}
                                                label={`Коммент. ${kindNumber} вида`}
                                                rules={requiredInputNumberRules}
                                            >
                                                <Input placeholder={''} />
                                            </Form.Item>
                                        </Col>
                                        <Col flex='1 1 100px'>
                                            <UploadFile
                                                name={sourceFileName}
                                                label={'Исходник от клиента'}
                                                disabled={sourceUploadDisabled}
                                            />
                                        </Col>
                                    </Row>
                                    {showDividerAfterLoPair ? <LoPairDivider /> : null}
                                </div>
                            )
                        })}
                        {hasCopiesSumError && (
                            <Typography.Text type='danger'>{errors[0]}</Typography.Text>
                        )}
                    </>
                )
            }}
        </Form.List>
    )
}

export default Positions
